/**
 * Format a FastAPI / Pydantic error detail into a human-readable
 * string. The shape varies by error class:
 *   - Plain HTTPException: detail is a string (use it directly)
 *   - Pydantic validation (422): detail is an array of objects shaped
 *     { type, loc, msg, input, ctx }. We surface field name(s) + msg.
 *   - Unknown shape: JSON-stringify so the user gets *something*
 *     useful rather than "[object Object]".
 */
function formatErrorDetail(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        // Field path comes back as ["body", "slug"] — drop the "body"
        // prefix that FastAPI prepends.
        const loc = Array.isArray(item.loc) ? item.loc.filter(p => p !== 'body').join('.') : '';
        const msg = item.msg || JSON.stringify(item);
        return loc ? `${loc}: ${msg}` : msg;
      }
      return String(item);
    }).join('; ');
  }
  if (typeof detail === 'object') {
    // 0.36.399 — Named-error dict from typed endpoints, e.g.
    //   { error: 'unknown_sovereign', field: 'sovereign', message: '...' }
    // Prefer the human message over JSON-stringifying the whole object.
    if (typeof detail.message === 'string') return detail.message;
    return JSON.stringify(detail);
  }
  return String(detail);
}


/* 0.41.98 - A session that has expired sends the admin to the login
 * page instead of showing "Not authenticated" over their work.
 *
 * Only for 401, and only for admin calls: a citizen filling a form
 * must never be bounced to a login page they have no business seeing.
 *
 * `next` carries where they were, so signing in returns them to the
 * same page rather than the admin home.
 *
 * Unsaved changes are still lost - the editor holds them in browser
 * state and there is nowhere to put them once the session is gone.
 * The redirect makes the CAUSE obvious, which the old error did not.
 */
function redirectToLogin() {
  if (typeof window === 'undefined') return;
  const here = window.location.pathname + window.location.search;
  // Already there: no loop, and no clobbering an existing `next`.
  if (window.location.pathname === '/login') return;
  window.location.href = `/login?next=${encodeURIComponent(here)}`;
}

async function request(path, options = {}) {
  const response = await fetch(path, {
    ...options,
    credentials: 'include',
    // 0.36.78 — Force a network round-trip on every admin API call.
    // Browsers may keep GET responses in the disk cache; for live
    // admin data (templates, languages, audit logs) that means stale
    // reads after a PATCH. `no-store` skips the cache entirely.
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });

  // Before anything else: an expired session is not an error to show,
  // it is a state to leave.
  if (response.status === 401 && path.startsWith('/api/admin/')) {
    redirectToLogin();
  }

  if (response.status === 204 || response.status === 205) {
    if (!response.ok) {
      const error = new Error(`Request failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  let body = null;
  if (isJson) {
    const text = await response.text();
    body = text ? JSON.parse(text) : null;
  }

  if (!response.ok) {
    const error = new Error(
      formatErrorDetail(body?.detail, `Request failed: ${response.status}`)
    );
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

export const adminLanguagesApi = {
  list: () => request('/api/admin/languages'),
  create: (data) => request('/api/admin/languages', {
    method: 'POST', body: JSON.stringify(data),
  }),
  update: (code, data) => request(`/api/admin/languages/${code}`, {
    method: 'PATCH', body: JSON.stringify(data),
  }),
  remove: (code, { force = false } = {}) => request(
    `/api/admin/languages/${code}${force ? '?force=true' : ''}`,
    { method: 'DELETE' }
  ),
  reorder: (codes) => request('/api/admin/languages/reorder', {
    method: 'PUT', body: JSON.stringify({ codes }),
  }),
};


// 0.41.06 - Registered outbound API connections.
//
// `save` sends the whole record. The `secret` field follows the SMTP
// convention: omit it to keep the stored value, send '' to clear it,
// send a value to replace it. The API never returns a secret, so a UI
// that always sent one would have to invent it.
/* 0.42.77 - Flows: an order over existing pages. */
// 0.44.06 - The site's navigation menu (first level).
export const adminNavMenuApi = {
  get: () => request('/api/admin/nav-menu'),
  save: (items, authoringLanguage, hamburger, colors) => request('/api/admin/nav-menu', {
    method: 'PUT',
    body: JSON.stringify({ items, authoring_language: authoringLanguage || 'en',
                           hamburger: hamburger || null, colors: colors || null }),
  }),
};

// 0.44.54 - Site content: the items of collections.
export const adminSiteContentApi = {
  cards: () => request('/api/admin/site-content'),
  info: (key) => request(`/api/admin/site-content/${encodeURIComponent(key)}`),
  // 0.46.45 - `table`: which table of the content library ('items' or
  // empty is the main one).
  items: (key, { q = '', status = '', table = '' } = {}) => request(
    `/api/admin/site-content/${encodeURIComponent(key)}/items?q=${encodeURIComponent(q)}&status=${encodeURIComponent(status)}${table ? `&table=${encodeURIComponent(table)}` : ''}`),
  get: (key, id) => request(`/api/admin/site-content/${encodeURIComponent(key)}/items/${id}`),
  create: (key, data, table = '') => request(`/api/admin/site-content/${encodeURIComponent(key)}/items${table ? `?table=${encodeURIComponent(table)}` : ''}`, { method: 'POST', body: JSON.stringify(data) }),
  update: (key, id, data) => request(`/api/admin/site-content/${encodeURIComponent(key)}/items/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (key, id) => request(`/api/admin/site-content/${encodeURIComponent(key)}/items/${id}`, { method: 'DELETE' }),
  // 0.45.19 - The hand-made order: every item id, in the new order.
  reorder: (key, ids, table = '') => request(`/api/admin/site-content/${encodeURIComponent(key)}/order${table ? `?table=${encodeURIComponent(table)}` : ''}`, { method: 'PUT', body: JSON.stringify({ ids }) }),
};

// 0.44.54 - The media store. A multipart upload, so not through request()
// (which sends JSON); errors take the same shape for serverFieldErrors.
export const adminMediaApi = {
  // 0.44.93 - allowSvg: the system icon takes an SVG (cleaned by the server).
  upload: async (file, kind = 'file', { allowSvg = false } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    if (allowSvg) fd.append('allow_svg', 'true');
    const response = await fetch('/api/admin/media', { method: 'POST', body: fd, credentials: 'include' });
    let body = null;
    try { body = await response.json(); } catch { body = null; }
    if (!response.ok) {
      const d = body && body.detail;
      const msg = Array.isArray(d) ? (d[0] && d[0].msg) : (typeof d === 'string' ? d
        : response.status === 413 ? 'The file is larger than the server accepts.' : `Upload failed: ${response.status}`);
      const error = new Error(msg || 'Upload failed.');
      error.status = response.status; error.body = body;
      throw error;
    }
    return body;
  },
};

// 0.44.51 - Icons added in Settings > Icons.
export const adminIconsApi = {
  list: () => request('/api/admin/icons'),
  create: (data) => request('/api/admin/icons', { method: 'POST', body: JSON.stringify(data) }),
  // 0.44.52 - svg optional: empty keeps the current drawing.
  update: (id, label, svg) => request(`/api/admin/icons/${id}`, { method: 'PUT', body: JSON.stringify({ label, svg: svg || null }) }),
  remove: (id) => request(`/api/admin/icons/${id}`, { method: 'DELETE' }),
};

// 0.44.50 - Collections: kinds of site content (definitions only).
export const adminCollectionsApi = {
  meta: () => request('/api/admin/collections/meta'),
  list: () => request('/api/admin/collections'),
  get: (id) => request(`/api/admin/collections/${id}`),
  create: (data) => request('/api/admin/collections', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/admin/collections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => request(`/api/admin/collections/${id}`, { method: 'DELETE' }),
};

// 0.49.83 - Data tables: tables the administrator designs.
export const adminDataTablesApi = {
  meta: () => request('/api/admin/data-tables/meta'),
  list: () => request('/api/admin/data-tables'),
  get: (key) => request(`/api/admin/data-tables/${encodeURIComponent(key)}`),
  plan: (change) => request('/api/admin/data-tables/plan', { method: 'POST', body: JSON.stringify(change) }),
  apply: (change) => request('/api/admin/data-tables/apply', { method: 'POST', body: JSON.stringify(change) }),
  patch: (key, data) => request(`/api/admin/data-tables/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(data) }),
  patchColumn: (key, col, data) => request(`/api/admin/data-tables/${encodeURIComponent(key)}/columns/${encodeURIComponent(col)}`,
    { method: 'PATCH', body: JSON.stringify(data) }),
  changes: (key) => request(`/api/admin/data-tables/${encodeURIComponent(key)}/changes`),
  rows: (key, params = {}) => request(`/api/admin/data-tables/${encodeURIComponent(key)}/rows?${new URLSearchParams(params)}`),
  addRow: (key, data) => request(`/api/admin/data-tables/${encodeURIComponent(key)}/rows`, { method: 'POST', body: JSON.stringify(data) }),
  editRow: (key, id, data) => request(`/api/admin/data-tables/${encodeURIComponent(key)}/rows/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  removeRow: (key, id) => request(`/api/admin/data-tables/${encodeURIComponent(key)}/rows/${id}`, { method: 'DELETE' }),
};

export const adminFlowsApi = {
  // 0.43.89 - The error situations a flow can have a page for.
  errorIds: () => request('/api/admin/flows/error-ids'),
  list: () => request('/api/admin/flows'),
  create: (data) => request('/api/admin/flows', {
    method: 'POST', body: JSON.stringify(data),
  }),
  update: (id, data) => request(`/api/admin/flows/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  remove: (id) => request(`/api/admin/flows/${id}`, { method: 'DELETE' }),
};

// 0.43.87 - AI provider presets (editable).
export const aiPresetsApi = {
  list: () => request('/api/admin/ai-presets'),
  create: (data) => request('/api/admin/ai-presets', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/admin/ai-presets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  reset: (id) => request(`/api/admin/ai-presets/${id}/reset`, { method: 'POST' }),
  remove: (id) => request(`/api/admin/ai-presets/${id}`, { method: 'DELETE' }),
};

export const adminApiConnectionsApi = {
  // 0.43.40 - kind 'general' or 'ai' for the two settings cards; no
  // kind lists every connection (the form editor's API pickers).
  list: (kind) => request(kind ? `/api/admin/api-connections?kind=${encodeURIComponent(kind)}` : '/api/admin/api-connections'),
  get: (id) => request(`/api/admin/api-connections/${id}`),
  create: (data) => request('/api/admin/api-connections', {
    method: 'POST', body: JSON.stringify(data),
  }),
  update: (id, data) => request(`/api/admin/api-connections/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  remove: (id) => request(`/api/admin/api-connections/${id}`, {
    method: 'DELETE',
  }),
  // 0.41.07 - The test request is described by the caller: method,
  // path, query rows, header rows and (for write verbs) a body.
  test: (id, req) => request(`/api/admin/api-connections/${id}/test`, {
    method: 'POST', body: JSON.stringify({
      path: (req && req.path) || '',
      method: (req && req.method) || 'GET',
      query: (req && req.query) || [],
      headers: (req && req.headers) || [],
      body: (req && req.body) || '',
      content_type: (req && req.contentType) || 'application/json',
      // 0.43.51 - The request under test; its Expected results record it.
      request_id: (req && req.requestId) || null,
      // 0.43.65 - Fill the saved request with these values, as a form
      // does, instead of sending the body above.
      use_template: !!(req && req.useTemplate),
      inputs: (req && req.inputs) || null,
      language: (req && req.language) || null,
    }),
  }),
  // 0.43.65 - The body a request would send with these values.
  previewRequest: (id, rid, values) => request(
    `/api/admin/api-connections/${id}/requests/${rid}/preview`, {
      method: 'POST', body: JSON.stringify({
        inputs: (values && values.inputs) || {}, language: (values && values.language) || null,
      }),
    }),

  // 0.43.51 - A connection's requests.
  listRequests: (id) => request(`/api/admin/api-connections/${id}/requests`),
  createRequest: (id, data) => request(`/api/admin/api-connections/${id}/requests`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  updateRequest: (id, rid, data) => request(`/api/admin/api-connections/${id}/requests/${rid}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  deleteRequest: (id, rid) => request(`/api/admin/api-connections/${id}/requests/${rid}`, {
    method: 'DELETE',
  }),
  resetRequestStructure: (id, rid) => request(
    `/api/admin/api-connections/${id}/requests/${rid}/reset-structure`, { method: 'POST' }),

  // 0.41.63 - Which forms use this connection, and forgetting the
  // recorded structure. Separate from the test because recording is
  // cumulative: a test only ever ADDS paths, so clearing one is a
  // deliberate act.
  usage: (id) => request(`/api/admin/api-connections/${id}/usage`),
  resetStructure: (id) => request(
    `/api/admin/api-connections/${id}/reset-structure`, { method: 'POST' }),

  // 0.41.69 - The response cache: what is held, and dropping it.
  cache: (id) => request(`/api/admin/api-connections/${id}/cache`),
  deleteCacheEntry: (id, entryId) => request(
    `/api/admin/api-connections/${id}/cache/${entryId}`, { method: 'DELETE' }),
  clearCache: (id) => request(
    `/api/admin/api-connections/${id}/cache`, { method: 'DELETE' }),
};

export const adminSettingsApi = {
  // 0.47.96 - Addresses (the public site under another site's path, admin by IP).
  getAddresses: () => request('/api/admin/settings/addresses'),
  saveAddresses: (data) => request('/api/admin/settings/addresses', {
    method: 'PUT', body: JSON.stringify(data),
  }),
  // 0.44.81 - IP access.
  getIpAccess: () => request('/api/admin/settings/ip-access'),
  saveIpAccess: (data) => request('/api/admin/settings/ip-access', {
    method: 'PUT', body: JSON.stringify(data),
  }),
  // 0.44.75 - System name and icon.
  getSystemIdentity: () => request('/api/admin/settings/system-identity'),
  saveSystemIdentity: (data) => request('/api/admin/settings/system-identity', {
    method: 'PUT', body: JSON.stringify(data),
  }),
  // 0.41.100 - The trusted root for ID card login.
  getIdCardRoot: () => request('/api/admin/settings/id-card-root'),
  saveIdCardRoot: (payload) => request('/api/admin/settings/id-card-root', {
    method: 'PUT',
    body: JSON.stringify(payload),
  }),
  addIdCardRoot: (pem) => request('/api/admin/settings/id-card-root/certificates', {
    method: 'POST',
    body: JSON.stringify({ root_ca_pem: pem }),
  }),
  deleteIdCardRoot: (fp) => request(
    `/api/admin/settings/id-card-root/certificates/${encodeURIComponent(fp)}`,
    { method: 'DELETE' }),
  setIdCardEnabled: (enabled) => request('/api/admin/settings/id-card-root/enabled', {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  }),

  getSmtp: () => request('/api/admin/settings/smtp'),
  saveSmtp: (data) => request('/api/admin/settings/smtp', {
    method: 'PUT', body: JSON.stringify(data),
  }),
  testSmtp: (to) => request('/api/admin/settings/smtp/test', {
    method: 'POST', body: JSON.stringify({ to }),
  }),
  getBranding: () => request('/api/admin/settings/branding'),
  saveBranding: (values) => request('/api/admin/settings/branding', {
    method: 'PUT', body: JSON.stringify({ values }),
  }),
  // 0.36.225 — Time & date format (system-wide defaults).
  getTimeFormat: () => request('/api/admin/settings/time-format'),
  saveTimeFormat: (data) => request('/api/admin/settings/time-format', {
    method: 'PUT', body: JSON.stringify(data),
  }),
  // 0.38.16 - Platform-wide number-format defaults. Baseline for
  // the cascade: expression `format(x, "...")` > per-form settings
  // > these platform defaults > raw String().
  // 0.46.77 - Buttons and focus.
  getUiTheme: () => request('/api/admin/settings/ui-theme'),
  // 0.47.14 - The last 12 custom colors applied, shared by all admins.
  getRecentColors: () => request('/api/admin/settings/recent-colors'),
  addRecentColor: (hex) => request('/api/admin/settings/recent-colors', {
    method: 'POST', body: JSON.stringify({ hex }),
  }),
  saveUiTheme: (data) => request('/api/admin/settings/ui-theme', {
    method: 'PUT', body: JSON.stringify(data),
  }),
  getNumberFormat: () => request('/api/admin/settings/number-format'),
  saveNumberFormat: (data) => request('/api/admin/settings/number-format', {
    method: 'PUT', body: JSON.stringify(data),
  }),
};

export const adminEmailTemplatesApi = {
  list: () => request('/api/admin/email-templates'),
  get: (slug) => request(`/api/admin/email-templates/${encodeURIComponent(slug)}`),
  updateLang: (slug, lang, data) => request(
    `/api/admin/email-templates/${encodeURIComponent(slug)}/${lang}`,
    { method: 'PUT', body: JSON.stringify(data) }
  ),
  test: (slug, to, languageCode) => request(
    `/api/admin/email-templates/${encodeURIComponent(slug)}/test`,
    { method: 'POST', body: JSON.stringify({ to, language_code: languageCode }) }
  ),
};

// 0.36.305 — Translations / Libraries tab data source. Returns the
// list of platform libraries that have at least one translatable
// column (the Libraries tab filters on translatability).
// 0.36.306 — listTags() returns the tag dropdown options for the
// Libraries-tab tag filter.
/* 0.49.59 - The site's user interface texts (Translations, tab User
   interface): every key with its text per language. */
export const adminUiTextsApi = {
  grid: () => request('/api/admin/translations'),
  save: (key, lang, value) => request(`/api/admin/translations/${encodeURIComponent(key)}/${encodeURIComponent(lang)}`,
    { method: 'PUT', body: JSON.stringify({ value }) }),
};

export const adminTranslationsLibrariesApi = {
  list: () => request('/api/admin/translations/libraries'),
  listTags: () => request('/api/admin/translations/search-tags'),
};

export const adminVerificationApi = {
  purposes: () => request('/api/admin/verification/purposes'),
  send: (email, purpose, languageCode) => request('/api/admin/verification/send', {
    method: 'POST',
    body: JSON.stringify({ email, purpose, language_code: languageCode }),
  }),
  verify: (email, purpose, code) => request('/api/admin/verification/verify', {
    method: 'POST',
    body: JSON.stringify({ email, purpose, code }),
  }),
};

export const adminGeoipApi = {
  get: () => request('/api/admin/settings/geoip'),
  save: (data) => request('/api/admin/settings/geoip', {
    method: 'PUT', body: JSON.stringify(data),
  }),
  refresh: () => request('/api/admin/settings/geoip/refresh', { method: 'POST' }),
  test: (ip) => request('/api/admin/settings/geoip/test', {
    method: 'POST', body: JSON.stringify({ ip }),
  }),
};

export const adminLanguageDeleteApi = {
  getUsage: (code) => request(`/api/admin/languages/${encodeURIComponent(code)}/usage`),
  deleteForce: (code) => request(`/api/admin/languages/${encodeURIComponent(code)}?force=true`, {
    method: 'DELETE',
  }),
};

export const adminTwoFactorApi = {
  get: () => request('/api/admin/settings/2fa'),
  save: (data) => request('/api/admin/settings/2fa', {
    method: 'PUT', body: JSON.stringify(data),
  }),
};

export const adminUsersApi = {
  list: (params = {}) => {
    // params: { q, role, is_active, is_verified, is_locked,
    //          two_factor_enabled, sort, direction, page, page_size }
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === '' || v === null || v === undefined) continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    return request(`/api/admin/users${s ? '?' + s : ''}`);
  },
  get: (id) => request(`/api/admin/users/${id}`),
  // ---- A.5.2 actions ----
  updateRoles: (id, roles) => request(`/api/admin/users/${id}/roles`, {
    method: 'PUT', body: JSON.stringify({ roles }),
  }),
  setActive: (id, is_active) => request(`/api/admin/users/${id}/active`, {
    method: 'PUT', body: JSON.stringify({ is_active }),
  }),
  lock: (id, reason) => request(`/api/admin/users/${id}/lock`, {
    method: 'POST', body: JSON.stringify({ reason: reason || null }),
  }),
  unlock: (id) => request(`/api/admin/users/${id}/unlock`, { method: 'POST' }),
  verify: (id) => request(`/api/admin/users/${id}/verify`, { method: 'POST' }),
  // 0.48.25 - Several at once (admins and yourself are skipped).
  // 0.48.26 - Accounts waiting for approval.
  bulkApprove: (public_ids) => request('/api/admin/users/bulk-approve', {
    method: 'POST', body: JSON.stringify({ public_ids }),
  }),
  // 0.48.37 - One user. The Users page's delete dialog called this,
  // but it did not exist ("remove is not a function"), so the single
  // delete did nothing. Server: DELETE /api/admin/users/{public_id}.
  remove: (publicId) => request(`/api/admin/users/${encodeURIComponent(publicId)}`, { method: 'DELETE' }),
  // 0.48.40 - Every email, name and social card number the user ID had.
  history: (publicId) => request(`/api/admin/users/${encodeURIComponent(publicId)}/history`),
  bulkRemove: (public_ids) => request('/api/admin/users/bulk-delete', {
    method: 'POST', body: JSON.stringify({ public_ids }),
  }),
  // 0.36.194 — Atomic patch for the redesigned Account tab. All
  // body fields optional; only the changed ones are sent. Returns
  // the freshly-computed AccountTabInfo so the caller can re-
  // snapshot dirty baseline without a second GET.
  patchProfile: (id, payload) => request(`/api/admin/users/${id}/profile`, {
    method: 'PATCH', body: JSON.stringify(payload),
  }),
  // 0.36.194 — Restoring the wrapper that was present in 0.36.121 and
  // lost in the 0.36.113-0.36.128 compressed-baseline window. The
  // backend endpoint has been live throughout. Kept around the
  // patchProfile call for any caller that wants to set SSN without
  // touching the rest of the profile.
  setSsn: (id, ssn) => request(`/api/admin/users/${id}/ssn`, {
    method: 'PUT', body: JSON.stringify({ ssn }),
  }),
  forcePasswordReset: (id) => request(`/api/admin/users/${id}/force-password-reset`, {
    method: 'POST',
  }),
  // 0.36.203 — Admin sets new password directly. Body: {new_password}.
  // Backend hashes, applies, revokes all sessions, clears lockout
  // state and any pending force-password-reset flag.
  setPassword: (id, newPassword) => request(`/api/admin/users/${id}/set-password`, {
    method: 'POST', body: JSON.stringify({ new_password: newPassword }),
  }),
  revokeSessions: (id) => request(`/api/admin/users/${id}/revoke-sessions`, {
    method: 'POST',
  }),
  // 0.36.208 — Reset failed-attempts counter (without unlocking
  // the account manually-locked state). Wired to the "reset" link
  // in the Security dashboard table on the Account tab.
  resetFailedAttempts: (id) => request(`/api/admin/users/${id}/reset-failed-attempts`, {
    method: 'POST',
  }),
  // 0.36.208 — Bulk-revoke all trusted devices for the user.
  // Wired to the "remove" link in the Security dashboard table.
  revokeAllTrustedDevices: (id) => request(`/api/admin/users/${id}/trusted-devices`, {
    method: 'DELETE',
  }),
  // ---- A.5.3 actions ----
  listTrustedDevices: (id) => request(`/api/admin/users/${id}/trusted-devices`),
  revokeTrustedDevice: (id, deviceId) => request(
    `/api/admin/users/${id}/trusted-devices/${deviceId}`,
    { method: 'DELETE' },
  ),
  reset2fa: (id) => request(`/api/admin/users/${id}/2fa/reset`, { method: 'POST' }),
  set2faRequired: (id, required) => request(`/api/admin/users/${id}/2fa/required`, {
    method: 'PUT', body: JSON.stringify({ required }),
  }),
};

export const adminHelpdeskApi = {
  list: () => request('/api/admin/helpdesk/email-change-requests'),
  pendingCount: () => request('/api/admin/helpdesk/pending-count'),
  approve: (requestId) => request(
    `/api/admin/helpdesk/email-change-requests/${requestId}/approve`,
    { method: 'POST' },
  ),
  deny: (requestId, admin_note) => request(
    `/api/admin/helpdesk/email-change-requests/${requestId}/deny`,
    { method: 'POST', body: JSON.stringify({ admin_note: admin_note || null }) },
  ),
  emailOverride: (userId, new_email, admin_note) => request(
    `/api/admin/helpdesk/users/${userId}/email-override`,
    { method: 'POST', body: JSON.stringify({ new_email, admin_note: admin_note || null }) },
  ),
};

export const adminAuditLogApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === '' || v === null || v === undefined) continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    return request(`/api/admin/audit-log${s ? '?' + s : ''}`);
  },
  actions: () => request('/api/admin/audit-log/actions'),
  // 0.36.577 — Cross-library imports list for the "Imported CSVs" tab.
  // Backend endpoint at /api/admin/imports; kept under this API object
  // so the tab UI has one place to look for related calls.
  importsList: (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === '' || v === null || v === undefined) continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    return request(`/api/admin/imports${s ? '?' + s : ''}`);
  },
  // 0.36.622 — CSV export of Library events tab (mirrors audit-log
  // Events tab exportCsv). Same q/since/action filters; response
  // headers X-Row-Capped + X-Row-Count.
  importsExportCsv: async (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === '' || v === null || v === undefined) continue;
      qs.set(k, String(v));
    }
    const url = `/api/admin/imports/export.csv${qs.toString() ? '?' + qs.toString() : ''}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.detail || `Export failed: ${response.status}`);
    }
    const capped = response.headers.get('X-Row-Capped') === '1';
    const rowCount = parseInt(response.headers.get('X-Row-Count') || '0', 10);
    const blob = await response.blob();
    return { blob, capped, rowCount };
  },
  // Direct URL for the download link (browser handles as file).
  importCsvUrl: (jobId) => `/api/admin/imports/${encodeURIComponent(jobId)}/csv`,
  // 0.36.577 — Retention window for uploaded CSV files. Value in days;
  // <=0 disables cleanup.
  importsSettings: () => request('/api/admin/imports/settings'),
  updateImportsSettings: (retention_days) => request(
    '/api/admin/imports/settings',
    { method: 'PATCH',
      body: JSON.stringify({ retention_days: Number(retention_days) }) },
  ),
  // CSV export — returns a Blob plus the X-Row-Capped header info
  exportCsv: async (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === '' || v === null || v === undefined) continue;
      qs.set(k, String(v));
    }
    const url = `/api/admin/audit-log/export.csv${qs.toString() ? '?' + qs.toString() : ''}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.detail || `Export failed: ${response.status}`);
    }
    const capped = response.headers.get('X-Row-Capped') === '1';
    const rowCount = parseInt(response.headers.get('X-Row-Count') || '0', 10);
    const blob = await response.blob();
    return { blob, capped, rowCount };
  },
};

// 0.36.338 — System diagnostics. Two endpoints (server resources +
// system logs), each fetched independently so the two tabs can
// refresh on their own.
export const adminDiagnosticsApi = {
  serverResources: () => request('/api/admin/diagnostics/server-resources'),
  systemLogs: () => request('/api/admin/diagnostics/system-logs'),
};

// 0.36.374 — adminIsoCodesApi, adminIsoCountriesApi,
// adminOfficialNamesApi, adminDialCodesApi removed. Their only
// consumers were the 4 dedicated ISO admin pages, which were
// retired in favor of AdminLibraryDetail's unified CRUD via
// adminFormLibrariesApi.detail/patchEntry/createEntry/deleteEntry.
// The public read-only surfaces `isoCodesApi` and `isoCountriesApi`
// stay — they're used by AdminLanguages for the language picker.

/** Public read-only — usable by any signed-in user; intended for autocomplete
 *  consumers like the Languages admin page. */
export const isoCodesApi = {
  list: ({ q = '' } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    const qs = params.toString();
    return request(`/api/iso-language-codes${qs ? '?' + qs : ''}`);
  },
};


// ============================================================
// A.5.4 — Invites and signup mode
// ============================================================

export const adminInvitesApi = {
  // 0.46.68 - toAdmin: the link opens the admin panel (else the public site).
  create: (email, roles, inviter_note, toAdmin = false) => request('/api/admin/invites', {
    method: 'POST',
    body: JSON.stringify({ email, roles, inviter_note: inviter_note || null, to_admin: !!toAdmin }),
  }),
  resend: (userId) => request(`/api/admin/invites/users/${userId}/resend`, {
    method: 'POST',
  }),
  cancel: (userId) => request(`/api/admin/invites/users/${userId}`, {
    method: 'DELETE',
  }),
};

/* 0.48.32 - Users > Sign-up attempts. */
export const adminSignupAttemptsApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== '' && v != null) qs.set(k, String(v));
    const s = qs.toString();
    return request(`/api/admin/signup-attempts${s ? '?' + s : ''}`);
  },
  reset: (ip) => request('/api/admin/signup-attempts/reset', {
    method: 'POST', body: JSON.stringify({ ip }),
  }),
};

export const adminSignupApi = {
  get: () => request('/api/admin/settings/signup'),
  // 0.48.25 - A boolean (open or invite only), or the whole settings
  // object with the protection (limit_per_hour, limit_per_day, bot_check).
  set: (v) => request('/api/admin/settings/signup', {
    method: 'PUT',
    body: JSON.stringify(v && typeof v === 'object' ? v : { public_signup_enabled: v }),
  }),
  // 0.48.85 - The sign-in update as typed, run for the admin's own
  // account; nothing is saved.
  testSignin: (cfg) => request('/api/admin/settings/signup/signin-update/test', {
    method: 'POST', body: JSON.stringify(cfg || {}),
  }),
};

export const inviteApi = {
  info: (token) => request(`/api/invites/${token}`),
  accept: (token, new_password) => request(`/api/invites/${token}/accept`, {
    method: 'POST',
    body: JSON.stringify({ token, new_password }),
  }),
};

export const publicSettingsApi = {
  get: () => request('/api/public/settings'),
};


// ============================================================
// 0.36.381 — cafApi and cafAdminApi removed with the CAF
// feature layer. The one non-CAF slice the phone widget needed
// (country list with dial codes) is served by /api/countries
// now. Callers: PageContentRenderer, AdminFormEditor,
// AdminFormPreview — all updated to use countriesApi.list().
// ============================================================

export const countriesApi = {
  // Returns the array directly: [{code, name, dial_code}, ...].
  // Was previously reached via cafApi.formOptions().countries.
  list: () => request('/api/countries'),
};

export const isoCountriesApi = {
  list: ({ q = '' } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    const qs = params.toString();
    return request(`/api/iso-country-codes${qs ? '?' + qs : ''}`);
  },
};


// 0.36.374 — adminIsoCountriesApi removed (was: admin CRUD for
// the ISO 3166-1 country codes library, only consumed by the
// retired AdminIsoCountryCodes page).


// 0.36.374 — adminOfficialNamesApi and adminDialCodesApi removed
// (unique consumers were the retired AdminIsoOfficialNames /
// AdminIsoDialCodes pages).


/**
 * Admin overview alerts — surfaces system issues needing attention.
 * Backed by /api/admin/alerts (see backend/app/routers/admin_alerts.py).
 */
export const adminAlertsApi = {
  list: () => request('/api/admin/alerts'),
};


/**
 * Admin endpoints for the static pages CMS.
 *
 * Used by the Admin > Pages section to manage About, FAQ, and any
 * future content pages. Translation upsert is a separate endpoint per
 * (page, language) — admin saves one language at a time from the
 * per-tab editor in AdminStaticPages.
 */
export const adminStaticPagesApi = {
  list: () => request('/api/admin/pages'),
  // 0.47.68 - A copy of the whole page or form (new address, not published).
  copy: (id) => request(`/api/admin/pages/${id}/copy`, { method: 'POST' }),
  get: (id) => request(`/api/admin/pages/${id}`),
  create: (data) => request('/api/admin/pages', {
    method: 'POST', body: JSON.stringify(data),
  }),
  update: (id, data) => request(`/api/admin/pages/${id}`, {
    method: 'PATCH', body: JSON.stringify(data),
  }),
  remove: (id) => request(`/api/admin/pages/${id}`, {
    method: 'DELETE',
  }),
  upsertTranslation: (id, lang, data) => request(
    `/api/admin/pages/${id}/translations/${lang}`,
    { method: 'PUT', body: JSON.stringify(data) },
  ),
  // 0.47.30 - A page file's other languages, after the import is saved.
  importTexts: (id, data) => request(`/api/admin/pages/${id}/import-texts`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  removeTranslation: (id, lang) => request(
    `/api/admin/pages/${id}/translations/${lang}`,
    { method: 'DELETE' },
  ),
  // 0.36.59 — Atomic landing swap. Demotes the current landing (if
  // any) and promotes the target. See backend docstring on
  // promote_to_landing for the full transition semantics.
  promoteToLanding: (id) => request(
    `/api/admin/pages/${id}/promote-to-landing`,
    { method: 'POST' },
  ),
};


/**
 * Admin Forms API (form constructor, 0.35.0).
 *
 * A form is a slug + name + JSON structure document. The editor saves
 * name and structure together as one PUT — atomic; partial saves don't
 * exist by design (a form is a coherent document).
 */
export const adminFormsApi = {
  list: () => request('/api/admin/forms'),
  get: (id) => request(`/api/admin/forms/${id}`),
  create: (data) => request('/api/admin/forms', {
    method: 'POST', body: JSON.stringify(data),
  }),
  update: (id, data) => request(`/api/admin/forms/${id}`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  remove: (id) => request(`/api/admin/forms/${id}`, { method: 'DELETE' }),
};

// 0.36.76 — Button templates: admin-saved presets for the Custom
// button variant. Each template stores a config (custom_* fields)
// keyed by a unique name; buttons reference templates via
// `variant: 'template:N'` in the page structure.
//
// Delete is protected against in-use templates. The DELETE endpoint
// returns 409 with body {message, template_id, template_name,
// in_use: true, pages: [{page_id, slug, title}]} when the template
// is referenced by any button on any page; the caller catches the
// thrown error and displays the page list.
//
// `getUsage` returns the same body shape without attempting deletion
// — useful for showing usage info preemptively (e.g., in a "Delete
// template" confirm dialog).
/* 0.48.74 - Cascade templates (settings store, by name). */
export const adminCascadeTemplatesApi = {
  list: () => request('/api/admin/cascade-templates'),
  save: (name, config) => request('/api/admin/cascade-templates', {
    method: 'PUT', body: JSON.stringify({ name, config }),
  }),
  remove: (name) => request(`/api/admin/cascade-templates?name=${encodeURIComponent(name)}`, { method: 'DELETE' }),
};

export const adminButtonTemplatesApi = {
  list: () => request('/api/admin/button-templates'),
  create: ({ name, config }) => request('/api/admin/button-templates', {
    method: 'POST',
    body: JSON.stringify({ name, config }),
  }),
  update: (id, { name, config }) => {
    const body = {};
    if (name !== undefined) body.name = name;
    if (config !== undefined) body.config = config;
    return request(`/api/admin/button-templates/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    });
  },
  remove: (id) => request(`/api/admin/button-templates/${id}`, {
    method: 'DELETE',
  }),
  getUsage: (id) => request(`/api/admin/button-templates/${id}/usage`),
};

// Public endpoint — used by the public page renderer (and by the
// editor's live preview). Doesn't require auth; returns the same
// shape as the admin list endpoint.
// 0.49.25 - Kept for five minutes. Pages no longer reload the site
// between them, and each page asked for the same list again.
let _btnTplCache = null;   // { at, promise, data }
export const buttonTemplatesPublicApi = {
  list: () => {
    const now = Date.now();
    if (_btnTplCache && now - _btnTplCache.at < 5 * 60 * 1000) return _btnTplCache.promise;
    const entry = { at: now, data: undefined, promise: null };
    entry.promise = request('/api/button-templates')
      .then((d) => { entry.data = d; return d; })
      .catch((e) => { if (_btnTplCache === entry) _btnTplCache = null; throw e; });
    _btnTplCache = entry;
    return entry.promise;
  },
  /** The list already fetched in this visit, or undefined. */
  cached: () => (_btnTplCache && Date.now() - _btnTplCache.at < 5 * 60 * 1000 ? _btnTplCache.data : undefined),
};


// 0.36.278 — Form-library client.
//
// adminFormLibrariesApi.list() returns the registry of available
// libraries with their schemas (column name + type) and a few sample
// rows for preview in the configurator's column picker.
//
// formLibraryRowsApi.list(key) is the PUBLIC endpoint used by the
// runtime renderers (AdminFormPreview, PageContentRenderer) to fetch
// every row of a chosen library and materialise it into dropdown
// options at form-render time.
export const adminFormLibrariesApi = {
  // 0.44.57 - { collections: true } also lists collections, as
  // read-only 'collection:<key>' libraries (pickers only; the Libraries
  // pages leave it off).
  list: ({ collections = false } = {}) => request(`/api/admin/form-libraries${collections ? '?collections=1' : ''}`),
  // 0.36.575 — Unified translation stats for both built-in and
  // custom libraries. Replaces the per-namespace
  // adminCustomLibrariesApi.translationStats() (still available for
  // backwards compat).
  translationStats: () => request('/api/admin/form-libraries/translation-stats'),
  // 0.36.355 — Generic references-by-key endpoint. Works for both
  // built-in libraries (LIBRARY_REGISTRY keys: 'iso_country_codes',
  // 'iso_language_codes', 'iso_official_names', ...) and custom
  // libraries. Used by the Usage column on the Libraries page (both
  // tabs) and by the dedicated references page.
  references: (key) => request(`/api/admin/form-libraries/${encodeURIComponent(key)}/references`),
  // 0.36.615 — Aggregate reference counts for ALL libraries in a
  // single call. Returns { <library_key>: <int_count>, ... }
  // covering both built-in and custom keys. Replaces the N-way
  // Promise.all fan-out of `references(key)` on the Libraries tab.
  referencesSummary: () => request('/api/admin/form-libraries/references-summary'),
  // 0.36.369 — Unified library detail. Works for both built-in and
  // custom keys. Returns {key, name, description, is_custom, pk,
  // columns:[...], entries:[...]}. Translatable columns come back
  // as per-language dicts so AdminLibraryDetail can switch tabs
  // without re-fetching.
  //
  // 0.36.536 — Server-side pagination + search. Accepts an
  // options object with `page`, `per_page`, `q`. When any of
  // these is set the response includes `total`, `page`,
  // `per_page` and the `entries` array is a slice, not the
  // whole library. Old callsites that pass just `key` still
  // get the full unpaginated response (backwards compat) —
  // useful for programmatic consumers and for the small
  // ref-library prefetch cache the constructor keeps.
  detail: (key, opts = null) => {
    const base = `/api/admin/form-libraries/${encodeURIComponent(key)}/detail`;
    if (!opts) return request(base);
    const params = new URLSearchParams();
    if (opts.page != null) params.set('page', String(opts.page));
    if (opts.per_page != null) params.set('per_page', String(opts.per_page));
    if (opts.q) params.set('q', opts.q);
    // 0.36.537 — Server-side sort. `sort` names the column (or 'id'
    // for the PK), `order` is 'asc' or 'desc'. `lang` picks which
    // language's value drives translatable-column sort.
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.order) params.set('order', opts.order);
    if (opts.lang) params.set('lang', opts.lang);
    // 0.36.576 — missing_lang drill-down.
    if (opts.missing_lang) params.set('missing_lang', opts.missing_lang);
    const qs = params.toString();
    return request(qs ? `${base}?${qs}` : base);
  },
  // 0.36.371 — Unified entry PATCH. Body shape:
  //   {
  //     data: {col: value, ...},                   // non-translatable + base of translatable
  //     translations: {col: {lang: value, ...}},   // non-English langs of translatable
  //   }
  // For built-in libraries, English is the base value (goes in `data`);
  // for custom libraries the base value in `data` is a fallback for
  // langs without translations, and any language (including English)
  // can be written via `translations`. Frontend routes writes to the
  // right slot based on the active language tab.
  patchEntry: (key, pk, payload) => request(
    `/api/admin/form-libraries/${encodeURIComponent(key)}/entries/${encodeURIComponent(pk)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  ),
  // 0.36.372 — Unified entry CREATE / DELETE. Same body shape as
  // patchEntry for create. Delete takes just the PK. Both work for
  // built-in AND custom libraries via server-side dispatch.
  createEntry: (key, payload) => request(
    `/api/admin/form-libraries/${encodeURIComponent(key)}/entries`,
    { method: 'POST', body: JSON.stringify(payload) },
  ),
  // 0.46.32 - Move a custom library's row one place up or down.
  moveEntry: (key, pk, direction) => request(
    `/api/admin/form-libraries/${encodeURIComponent(key)}/entries/${encodeURIComponent(pk)}/move`,
    { method: 'POST', body: JSON.stringify({ direction }) },
  ),
  deleteEntry: (key, pk) => request(
    `/api/admin/form-libraries/${encodeURIComponent(key)}/entries/${encodeURIComponent(pk)}`,
    { method: 'DELETE' },
  ),
  // 0.36.400 — Pre-delete check. Returns { can_delete, blocker_message,
  // dependent_codes }. Frontend calls this before opening the Delete
  // confirmation dialog so the admin sees WHY a delete would fail
  // BEFORE clicking Confirm. Only iso_official_names has real block
  // rules today (sovereign with dependent country codes); other
  // libraries always come back can_delete=true.
  deletePreview: (key, pk) => request(
    `/api/admin/form-libraries/${encodeURIComponent(key)}/entries/${encodeURIComponent(pk)}/delete-preview`,
  ),
  // 0.36.380 — CSV export. Returns a Blob to trigger a download.
  // lang_mode = 'single' | 'all' | 'with_authoring'. Server sets
  // Content-Disposition; caller reads response.blob() and pushes
  // to a temporary anchor element.
  exportCsv: async (key, { lang_mode = 'all', lang } = {}) => {
    const params = new URLSearchParams();
    params.set('lang_mode', lang_mode);
    if (lang) params.set('lang', lang);
    const response = await fetch(
      `/api/admin/form-libraries/${encodeURIComponent(key)}/export.csv?${params.toString()}`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.detail || `Export failed: ${response.status}`);
    }
    // Try to pull the server-suggested filename from the header.
    const cd = response.headers.get('content-disposition') || '';
    const fnMatch = cd.match(/filename="?([^"]+)"?/i);
    const filename = fnMatch ? fnMatch[1] : `${key}.csv`;
    return { blob: await response.blob(), filename };
  },
  // 0.36.380 — Multipart import. Accepts CSV/XLSX/XLS. Server
  // parses, validates, and returns {created, updated, skipped,
  // errors}. Non-2xx surfaces as thrown Error.
  importFile: async (key, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const response = await fetch(
      `/api/admin/form-libraries/${encodeURIComponent(key)}/import`,
      { method: 'POST', credentials: 'include', body: fd },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.detail || `Import failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return body;
  },
  // 0.36.512 — Background CSV import job API. Upload endpoint
  // returns the fresh job row immediately; caller polls the status
  // endpoint every 1-2 seconds to render the progress bar. Import
  // work runs on a backend worker thread; frontend can close the
  // modal / tab / browser without stopping the import.
  // 0.47.60 - mode: 'merge' (default) or 'replace' (rows not in the
  // file are removed).
  createImportJob: async (key, file, mode = 'merge') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', mode);
    const response = await fetch(
      `/api/admin/form-libraries/${encodeURIComponent(key)}/imports`,
      { method: 'POST', credentials: 'include', body: fd },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.detail || `Upload failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return body;
  },
  getImportJob: async (key, jobId) => {
    const response = await fetch(
      `/api/admin/form-libraries/${encodeURIComponent(key)}/imports/${jobId}`,
      { credentials: 'include' },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.detail || `Job status failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return body;
  },
  listImportJobs: async (key) => {
    const response = await fetch(
      `/api/admin/form-libraries/${encodeURIComponent(key)}/imports`,
      { credentials: 'include' },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.detail || `Job list failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return body;
  },
  cancelImportJob: async (key, jobId, action) => {
    // action: 'keep' | 'rollback'
    const response = await fetch(
      `/api/admin/form-libraries/${encodeURIComponent(key)}/imports/${jobId}/cancel`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.detail || `Cancel failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return body;
  },
  // 0.36.512 — Cross-library list of active + recently-completed
  // imports. Feeds the admin banner.
  listActiveImports: async () => {
    const response = await fetch(
      '/api/admin/imports/active',
      { credentials: 'include' },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.detail || `Active imports failed: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return body;
  },
  // 0.36.382 — Custom-library settings. Currently one field:
  // id_policy. When switching to 'auto_compact' with gaps
  // present, the backend returns 409 with a body describing
  // the gaps; caller shows a ConfirmDialog and retries with
  // compact_confirmed=true.
  patchSettings: async (key, patch, { compactConfirmed = false } = {}) => {
    const url = `/api/admin/form-libraries/${encodeURIComponent(key)}/settings${
      compactConfirmed ? '?compact_confirmed=true' : ''
    }`;
    const response = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(
        typeof body.detail === 'string' ? body.detail : (body.detail?.message || `Settings update failed: ${response.status}`),
      );
      err.status = response.status;
      err.detail = body.detail;  // Structured payload for the compaction confirm dialog
      throw err;
    }
    return body;
  },

  // 0.36.485 — Read-only pre-check for the Reset IDs flow. Returns
  // `{ blocked: bool, blockers: [{source, column, kind}...], message? }`.
  // Called before opening the Reset IDs confirm dialog so admin can
  // see a specific blocker message when other libraries reference
  // this one, instead of confirming and then hitting the 409.
  resetIdsCheck: async (key) => {
    const url = `/api/admin/form-libraries/${encodeURIComponent(key)}/reset-ids-check`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(
        typeof body.detail === 'string'
          ? body.detail
          : (body.detail?.message || `Reset IDs check failed: ${response.status}`),
      );
      err.status = response.status;
      err.detail = body.detail;
      throw err;
    }
    return body;
  },

  // 0.36.479 — Manual one-shot renumber. Backend renumbers all rows
  // to a contiguous 1..N sequence by current business_id ASC. Blocks
  // with 409 if the library is referenced by another library's
  // reference column (renumbering would invalidate the pointers).
  resetIds: async (key) => {
    const url = `/api/admin/form-libraries/${encodeURIComponent(key)}/reset-ids`;
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(
        typeof body.detail === 'string'
          ? body.detail
          : (body.detail?.message || `Reset IDs failed: ${response.status}`),
      );
      err.status = response.status;
      err.detail = body.detail;
      throw err;
    }
    return body;
  },
};

export const formLibraryRowsApi = {
  // 0.36.278 / 0.36.284 — `lang` (optional) triggers per-row
  // translation lookup on the backend; columns declared
  // translatable in LIBRARY_REGISTRY come back in that language,
  // with the raw column value as fallback when no translation
  // exists. Omit to get raw values.
  list: (key, { lang } = {}) => {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    return request(`/api/form-libraries/${encodeURIComponent(key)}/rows${qs}`);
  },
  // 0.37.93 — Point lookup for `lib(libKey, keyColumn, keyValue,
  // valueColumn)` expressions. Fetches ONE row instead of the whole
  // library. ~200-byte response vs ~2MB for full libraries; makes
  // label/hint resolution feel instant even for large libraries
  // (e.g. goods_services_nice_classification has 10k+ rows).
  //
  // Returns `{ row: {colName: value, ...} }` on success. Throws with
  // a `.status === 404` shape when the library or the row is missing —
  // callers should treat 404 as "lookup returned undefined" and render
  // an empty string.
  row: (key, { keyColumn, keyValue, lang } = {}) => {
    const params = new URLSearchParams({
      keyColumn: String(keyColumn),
      keyValue: String(keyValue),
    });
    if (lang) params.set('lang', lang);
    return request(`/api/form-libraries/${encodeURIComponent(key)}/row?${params.toString()}`);
  },
  // 0.37.97 — Batch point-lookup. Resolves N lookups in one HTTP
  // call. Frontend groups literal =lib() references per (libKey,
  // lang) and calls this once per group.
  //
  //   payload.lookups = [{keyColumn, keyValue, valueColumn}, ...]
  //   payload.lang    = 'hy' | undefined
  //
  //   response.results = [{keyColumn, keyValue, valueColumn, row}]
  //     (row is null when not found; order matches request)
  batchLookup: (key, { lookups, lang } = {}) => {
    return request(`/api/form-libraries/${encodeURIComponent(key)}/batch-lookup`, {
      method: 'POST',
      body: JSON.stringify({ lookups, lang: lang || null }),
    });
  },
};


// 0.36.278 — Dropdown behavior thresholds (admin only).
// Two integers: inline_threshold (≤ → expand to radio/checkbox) and
// search_threshold (> → show search inside dropdown). See
// frontend/src/utils/dropdownThresholds.js for runtime consumption.
export const adminDropdownThresholdsApi = {
  get: () => request('/api/admin/settings/dropdown-thresholds'),
  update: ({ inline_threshold, search_threshold }) =>
    request('/api/admin/settings/dropdown-thresholds', {
      method: 'PUT',
      body: JSON.stringify({ inline_threshold, search_threshold }),
    }),
};

// 0.36.285 — Custom library CRUD. Admin-defined dropdown source
// datasets (sister to the hardcoded builtins in form_libraries.py).
// 0.36.613 — Cleaned up. Removed 9 dead wrappers (get, patch, usage,
// references, translationStats, listEntries, addEntry, patchEntry,
// deleteEntry) that had zero JSX callers. Their backend routes were
// deleted in the same ship. Everything remaining is actively called
// from admin UI code paths (verified via grep).
export const adminCustomLibrariesApi = {
  list: () => request('/api/admin/custom-libraries'),
  // 0.47.61 - Whole libraries (settings, columns, rows with their IDs
  // and translations) as one JSON file, and back.
  exportFile: (keys) => request('/api/admin/custom-libraries/export', {
    method: 'POST', body: JSON.stringify({ keys }),
  }),
  importFile: (file, keys, onConflict) => request('/api/admin/custom-libraries/import', {
    method: 'POST', body: JSON.stringify({ file, keys, on_conflict: onConflict }),
  }),
  create: (data) => request('/api/admin/custom-libraries', {
    method: 'POST', body: JSON.stringify(data),
  }),
  remove: (key) => request(`/api/admin/custom-libraries/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  }),
  // 0.38.12 - Restored. Returns the list of page references for
  // this library (structural OR expression). Consumers use
  // `.references.length` as the in-use count. The 0.36.613
  // cleanup removed this wrapper along with its backend endpoint;
  // the list view's usage badge relied on it via a try/catch and
  // every library was silently showing 0 (Not in use). Backend
  // endpoint restored in the same 0.38.12 ship.
  usage: (key) => request(`/api/admin/custom-libraries/${encodeURIComponent(key)}/usage`),

  // Columns
  addColumn: (key, data) => request(`/api/admin/custom-libraries/${encodeURIComponent(key)}/columns`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  patchColumn: (key, colId, data) => request(
    `/api/admin/custom-libraries/${encodeURIComponent(key)}/columns/${colId}`,
    { method: 'PATCH', body: JSON.stringify(data) }
  ),
  deleteColumn: (key, colId) => request(
    `/api/admin/custom-libraries/${encodeURIComponent(key)}/columns/${colId}`,
    { method: 'DELETE' }
  ),
};

// 0.36.293 — Platform layout (header / footer) configuration.
// Each is fetched / patched independently for the admin screens;
// the citizen side uses a separate public fetcher that returns
// both in one call (see /api/layout in src/api/layout.js).
export const adminLayoutApi = {
  get: (key) => request(`/api/admin/layout/${key}`),
  put: (key, config) => request(`/api/admin/layout/${key}`, {
    method: 'PUT', body: JSON.stringify(config),
  }),
};
